import { useState } from 'preact/hooks';
import { useStore } from '../store';
import strategyData from '../../../data/breeding-strategies.json';
import { Sheet } from './Sheet';
import { PairStatusLine, usePairRequest } from './PairStatus';
import { coatLabel, type UiAnimal } from '../bridge';

/**
 * Mate candidates for one animal: same species, opposite sex, alive, same enclosure first.
 * Inbreeding risk and offspring coat odds appear only when the sim projects them (ui.mgmt.breeding.pairs);
 * the pedigree appears only when ui.mgmt.breeding.pedigree is projected. Nothing is estimated here.
 * Pair sends breeding.pair.request with explicit sireId and damId; the herd card shows the result.
 */
function riskLabel(bp: number): { text: string; cls: string } {
  if (bp >= 1250) { return { text: `High risk ${Math.round(bp / 100)}%`, cls: 'pill pill-red' }; }
  if (bp >= 625) { return { text: `Some risk ${Math.round(bp / 100)}%`, cls: 'pill pill-amber' }; }
  return { text: bp === 0 ? 'No inbreeding' : `Low risk ${Math.round(bp / 100)}%`, cls: 'pill' };
}

export function PairingSheet({ a, onClose, onPaired }: { a: UiAnimal; onClose(): void; onPaired(): void }) {
  const { sim } = useStore();
  const pairReq = usePairRequest();
  const mates = sim.animals
    .filter((m) => m.id !== a.id && m.species === a.species && m.sex !== a.sex && m.health > 0)
    .sort((x, y) => Number(y.enclosure === a.enclosure) - Number(x.enclosure === a.enclosure));
  const [strategy, setStrategy] = useState<'balanced' | 'line' | 'outcross'>('balanced');
  const done = new Set([...(sim.researchTree ? sim.researchTree.completed : []), ...sim.research.completed]);
  const strategies = strategyData.strategies;
  const chosen = strategies.find((x) => x.id === strategy);
  const name = (id: string | null): string => (id === null ? 'not recorded' : sim.animals.find((x) => x.id === id)?.name ?? id);
  const ped = sim.breeding?.pedigree;
  const parents = ped?.[a.id];
  return (
    <Sheet label={`Mates for ${a.name}`} onClose={onClose}>
      <div style="padding:6px 18px 16px">
        <div class="eyebrow eyebrow-rule">Mates for {a.name}</div>
        <div class="eyebrow" style="margin-top:8px">Breeding approach</div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:6px">
          {strategies.map((x) => {
            const locked = x.requires_research !== null && !done.has(x.requires_research);
            return <button key={x.id} class={x.id === strategy ? 'btn btn-primary' : 'btn btn-quiet'} style="padding:5px 12px;font-size:12px" disabled={locked} onClick={() => { setStrategy(x.id as 'balanced' | 'line' | 'outcross'); }}>{x.label}{locked ? ' (locked)' : ''}</button>;
          })}
        </div>
        {chosen && (
          <div class="row-label" style="white-space:normal;text-transform:none;letter-spacing:0.01em;margin-top:6px">
            {chosen.blurb} Pros: {chosen.pros.join('; ')}. Cons: {chosen.cons.join('; ')}.
          </div>
        )}
        {strategies.filter((x) => x.requires_research !== null && !done.has(x.requires_research)).map((x) => (
          <div key={x.id} class="row-label" style="white-space:normal;text-transform:none;letter-spacing:0.01em;margin-top:4px">{x.label} needs the {String(x.requires_research).replace(/_/g, ' ')} study in Research.</div>
        ))}
        {ped && (
          <div class="row-label" style="white-space:normal;text-transform:none;letter-spacing:0.01em;margin-top:8px">
            Parents: {parents ? `${name(parents.sire)} (sire), ${name(parents.dam)} (dam)` : 'not recorded (founder)'}
          </div>
        )}
        <div class="row-label" style="white-space:normal;text-transform:none;letter-spacing:.01em;margin-top:8px">{a.bornTick===undefined?'Age unavailable':`Age ${Math.max(0,Math.floor((sim.tick-a.bornTick)/24))} game days`}. {a.maturityDays===undefined?'Breeding maturity unavailable.':`Breeding maturity ${a.maturityDays} days.`} Pairing also checks health and housing. If the sire changes pens, he must be released from quarantine before moving. This is not a ban on same-pen conception.</div>
        <PairStatusLine phase={pairReq.phase} />
        {mates.length === 0 && <p style="font-size:13px;color:var(--ui-ink-soft)">No living {a.sex === 'F' ? 'male' : 'female'} {a.species} in the herd. Check the dealer for a compatible mate.</p>}
        {mates.map((m) => {
          const sireId = a.sex === 'M' ? a.id : m.id;
          const damId = a.sex === 'M' ? m.id : a.id;
          const pair = sim.breeding?.pairs?.find((x) => x.sireId === sireId && x.damId === damId);
          const refusal = pair?.refusalByStrategy?.[strategy];
          const risk = pair ? riskLabel(pair.inbreedingBp) : null;
          const mp = ped?.[m.id];
          return (
            <div key={m.id} class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:8px;padding:10px 12px">
              <div style="display:flex;align-items:center;gap:8px">
                <span class="row-main"><div class="row-value">{m.name}</div><div class="row-label">{coatLabel(m.species, m.coat)} - {m.enclosure === a.enclosure ? 'same pen' : 'other pen'}</div></span>
                <button class="btn btn-primary" style="padding:6px 14px;font-size:12px" disabled={pairReq.pending || Boolean(refusal)} onClick={() => { pairReq.send(sireId, damId, strategy); onPaired(); }}>{pairReq.pending ? 'Pending' : 'Pair'}</button>
              </div>
              {refusal && <div class="row-label" style="white-space:normal;text-transform:none;letter-spacing:.01em;margin-top:6px" role="status">Cannot pair now: {refusal}.</div>}
              {risk && <span class={risk.cls} style="display:inline-block;margin-top:6px">{risk.text}</span>}
              {pair?.odds && pair.odds.length > 0 && (
                <div class="row-label" style="white-space:normal;text-transform:none;letter-spacing:0.01em;margin-top:4px">
                  Offspring coat odds: {pair.odds.map((o) => `${o.coat} ${Math.round(o.bp / 100)}%`).join(', ')}
                </div>
              )}
              {ped && <div class="row-label" style="white-space:normal;text-transform:none;letter-spacing:0.01em;margin-top:2px">Parents: {mp ? `${name(mp.sire)}, ${name(mp.dam)}` : 'founder'}</div>}
              {!pair && <div class="row-label" style="margin-top:4px;text-transform:none;letter-spacing:0.01em">Risk and odds: not provided by the sim yet.</div>}
            </div>
          );
        })}
      </div>
    </Sheet>
  );
}
